import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Modal, Input, Button, Typography, Space, Divider } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";

const { TextArea } = Input;
const { Paragraph, Text } = Typography;

function normalizeHighlights(highlights) {
  if (!Array.isArray(highlights) || !highlights.length) {
    return [{ excerpt: "", note: "" }];
  }
  return highlights.map((item) => ({
    excerpt: item?.excerpt || "",
    note: item?.note || "",
  }));
}

export default function UnapprovalFeedbackModal({
  open,
  postTitle,
  initialFeedback,
  confirmLoading = false,
  onCancel,
  onSubmit,
  modalTitle = "Provide Revision Guidance",
  submitLabel = "Submit Feedback",
}) {
  const [message, setMessage] = useState("");
  const [highlights, setHighlights] = useState(() => normalizeHighlights());
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    setMessage(initialFeedback?.message || "");
    setHighlights(normalizeHighlights(initialFeedback?.highlights));
    setError(null);
  }, [open, initialFeedback]);

  const trimmedMessage = useMemo(() => message.trim(), [message]);

  const handleChangeHighlight = (index, key, value) => {
    setHighlights((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        [key]: value,
      };
      return next;
    });
  };

  const handleRemoveHighlight = (index) => {
    setHighlights((prev) => {
      if (prev.length === 1) {
        return [{ excerpt: "", note: "" }];
      }
      return prev.filter((_, idx) => idx !== index);
    });
  };

  const handleAddHighlight = () => {
    setHighlights((prev) => [...prev, { excerpt: "", note: "" }]);
  };

  const handleSubmit = () => {
    if (!trimmedMessage) {
      setError("Please provide guidance for the author.");
      return;
    }

    const cleanedHighlights = highlights
      .map((item) => ({
        excerpt: (item?.excerpt || "").trim(),
        note: (item?.note || "").trim(),
      }))
      .filter((item) => item.excerpt || item.note);

    onSubmit({
      message: trimmedMessage,
      highlights: cleanedHighlights,
    });
  };

  return (
    <Modal
      className="[&_.ant-modal-content]:dark:bg-[#161616] [&_.ant-modal-header]:dark:bg-[#161616] [&_.ant-modal-header]:dark:border-gray-800 [&_.ant-modal-title]:dark:text-white [&_.ant-modal-title]:blog-title [&_.ant-modal-title]:text-2xl [&_.ant-modal-close]:dark:text-gray-400 [&_.ant-modal-close:hover]:dark:text-white [&_.ant-modal-footer]:dark:bg-[#161616] [&_.ant-modal-footer]:dark:border-gray-800 [&_.ant-input]:dark:!bg-[#0E0E0E] [&_.ant-input]:dark:!border-gray-800 [&_.ant-input]:dark:!text-white [&_.ant-input]:dark:!placeholder-gray-500 [&_.ant-input-textarea-show-count]:dark:!text-gray-500 [&_.ant-typography]:dark:!text-gray-300 [&_.ant-typography-secondary]:dark:!text-gray-400 [&_.ant-divider]:dark:!border-gray-800 [&_.ant-btn-dashed]:dark:!border-gray-700 [&_.ant-btn-dashed]:dark:!text-gray-300 [&_.ant-btn-dashed:hover]:dark:!border-primary [&_.ant-btn-dashed:hover]:dark:!text-primary [&_.ant-btn-text]:dark:!text-gray-300 [&_.ant-modal-footer_.ant-btn-primary]:!bg-primary [&_.ant-modal-footer_.ant-btn-primary]:!border-primary [&_.ant-modal-footer_.ant-btn-primary:hover]:!bg-[#229bb8] [&_.ant-modal-footer_.ant-btn-default]:dark:!bg-[#0E0E0E] [&_.ant-modal-footer_.ant-btn-default]:dark:!border-gray-800 [&_.ant-modal-footer_.ant-btn-default]:dark:!text-gray-300"
      open={open}
      title={modalTitle}
      okText={submitLabel}
      onOk={handleSubmit}
      onCancel={onCancel}
      confirmLoading={confirmLoading}
      destroyOnHidden
      width={720}
    >
      <Space direction="vertical" size="middle" className="w-full">
        <Paragraph type="secondary" className="mb-0">
          Let the author know exactly what needs to change before this post can go
          live. Your message is required. Use highlights to call out specific
          sections when it helps illustrate the issue.
        </Paragraph>
        {postTitle && (
          <Paragraph className="mb-0">
            <Text strong>Post:</Text> {postTitle}
          </Paragraph>
        )}
        <div>
          <Text strong className="block mb-2">
            Guidance message
          </Text>
          <TextArea
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            rows={4}
            className="dark:bg-[#0E0E0E] dark:border-gray-800 dark:text-white"
            placeholder="Explain what needs to be fixed so the author can make progress."
            maxLength={2000}
            showCount
          />
          {error && (
            <Text type="danger" className="mt-1 block text-sm">
              {error}
            </Text>
          )}
        </div>
        <Divider className="my-2" />
        <div>
          <div className="flex items-center justify-between mb-2">
            <Text strong>Highlights (optional)</Text>
            <Button
              type="dashed"
              size="small"
              icon={<PlusOutlined />}
              onClick={handleAddHighlight}
            >
              Add highlight
            </Button>
          </div>
          <Space direction="vertical" size="middle" className="w-full">
            {highlights.map((item, index) => (
              <div
                key={`highlight-${index}`}
                className="rounded-lg border border-gray-200 dark:border-gray-800 p-3 bg-white dark:bg-[#0E0E0E]"
              >
                <Space direction="vertical" className="w-full">
                  <TextArea
                    value={item.excerpt}
                    onChange={(event) =>
                      handleChangeHighlight(index, "excerpt", event.target.value)
                    }
                    rows={5}
                    placeholder="Excerpt or summary of the section to fix"
                    maxLength={500}
                    showCount
                    className="mb-4"
                  />
                  <Input
                    value={item.note}
                    onChange={(event) =>
                      handleChangeHighlight(index, "note", event.target.value)
                    }
                    placeholder="Optional note describing why this section needs attention"
                    maxLength={500}
                  />
                  <div className="flex justify-end">
                    <Button
                      danger
                      type="text"
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemoveHighlight(index)}
                      disabled={highlights.length === 1 && !item.excerpt && !item.note}
                    >
                      Remove
                    </Button>
                  </div>
                </Space>
              </div>
            ))}
          </Space>
        </div>
      </Space>
    </Modal>
  );
}

UnapprovalFeedbackModal.propTypes = {
  open: PropTypes.bool.isRequired,
  postTitle: PropTypes.string,
  initialFeedback: PropTypes.shape({
    message: PropTypes.string,
    highlights: PropTypes.arrayOf(
      PropTypes.shape({
        excerpt: PropTypes.string,
        note: PropTypes.string,
      })
    ),
  }),
  confirmLoading: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  modalTitle: PropTypes.string,
  submitLabel: PropTypes.string,
};
